import { FaLinkedin, FaInstagram, FaFacebook } from "react-icons/fa";
import React, { useEffect, useRef, useState } from 'react';
import "./Home.css";

// MapEmbed - lazy loads the Google Maps iframe when the map container enters the viewport.
function MapEmbed() {
  const containerRef = useRef(null);
  const [src, setSrc] = useState('');
  const [loaded, setLoaded] = useState(false);

  const MAP_URL = 'https://www.google.com/maps?q=Sonegiri%20Kan,%20Bikaner,%20Rajasthan,%20India&output=embed';

  useEffect(() => {
    let observer;
    // If already visible, set src immediately
    const node = containerRef.current;
    if (!node) return;

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setSrc(MAP_URL);
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(node);
    } else {
      // Fallback: load after short delay
      const t = setTimeout(() => setSrc(MAP_URL), 1000);
      return () => clearTimeout(t);
    }

    // Safety fallback: if observer didn't trigger, load after 3s
    const fallback = setTimeout(() => {
      if (!src) setSrc(MAP_URL);
    }, 3000);

    return () => {
      if (observer) observer.disconnect();
      clearTimeout(fallback);
    };
  }, []); // run once

  return (
    <div ref={containerRef} className="w-full h-full relative">
      {/* Skeleton / placeholder while loading */}
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <div className="text-center">
            <div className="inline-block w-16 h-16 rounded-full border-4 border-amber-400 border-t-transparent animate-spin mb-4" />
            <div className="text-sm text-gray-600 dark:text-gray-300">Loading map…</div>
          </div>
        </div>
      )}

      {src && (
        <iframe
          title="Trivana Location"
          src={src}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={`w-full h-full transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
}

function Contact() {
  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center animate-fade-in-up">
            <div className="inline-block px-6 py-2 bg-gradient-to-r from-[#4299f5]/20 to-[#2d7dd1]/20 text-[#4299f5] rounded-full font-semibold text-sm mb-6 border border-[#4299f5]/30">
              Get In Touch
            </div>
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl mb-6" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Contact <span className="text-[#4299f5]">Us</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Reach out to Trivana Hospitality LLP for any queries or collaboration.
            </p>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-40 h-40 bg-[#4299f5]/20 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-60 h-60 bg-[#4299f5]/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
      </section>

      {/* Contact Info Section */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">

        {/* Contact Info Card */}
        <div className="mb-10 mx-auto max-w-xl bg-white rounded-2xl shadow-lg hover:shadow-2xl border-2 border-transparent hover:border-[#4299f5] transition-all duration-300 p-8 text-center animate-fade-in-up delay-200">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] shadow-lg mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>Email</h3>
            <a
              href="mailto:trivanahospitality@gmail.com"
              className="text-[#4299f5] text-lg font-medium hover:text-[#3a8fe5] transition-colors"
            >
              trivanahospitality@gmail.com
            </a>
          </div>
          
          <div className="mb-6 pt-6 border-t border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] shadow-lg mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>Address</h3>
            <p className="text-gray-600 text-lg">
              C/o Vimla Devi, Bulaki Chand Banthia, Near Songiri Well, Bikaner, Bikaner, Bikaner, Rajasthan, India, 334001
            </p>
          </div>
          
          {/* Social Links */}
          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>Follow Us</h3>
            <div className="flex justify-center space-x-6 text-2xl">
            <a
              href="https://www.linkedin.com/company/trivana-hospitality"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
              title="LinkedIn"
            >
              <FaLinkedin className="text-white text-xl" />
            </a>
            <a
              href="https://www.instagram.com/trivanahospitality/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 transition-all duration-300 transform hover:scale-110 shadow-lg"
              title="Instagram"
            >
              <FaInstagram className="text-white text-xl" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61574583771198"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 transition-all duration-300 transform hover:scale-110 shadow-lg"
              title="Facebook"
            >
              <FaFacebook className="text-white text-xl" />
            </a>
          </div>
        </div>
        </div>

        {/* Map */}
        <div className="animate-fade-in-up delay-300">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-6" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Find Us On <span className="text-[#4299f5]">Map</span>
          </h3>
          <div
            className="w-full h-96 rounded-2xl overflow-hidden shadow-lg border-2 border-gray-200 hover:border-[#4299f5] transition-all duration-300 relative"
          >
            <MapEmbed />
          </div>
        </div>
      </div>
      </section>
    </div>
  );
}

export default Contact;
